import { commonUtils } from '@/helpers/commonUtils'
import PptPage, { PageHeader } from '../PptPage/PptPage'
import styles from './PptPage4.module.less'
import { SliderModels } from '@/types/slider'
import { SyntheticEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GENDER_MAPS, INDUSTRY_CATEGORY_MAPS, TARGET_MARKTS_MAPS } from '@/i18nEnumConsts'
import cls from 'classnames'
import { getIndustryDisplayName } from '@/helpers/industry'

interface Props {
  project: ProjectModels.ProjectDetail
  no: number
  onSliderReady: (slider: SliderModels.Slider, no: number) => void
}

function PptPage4({ project, no, onSliderReady }: Props) {
  const { t, i18n } = useTranslation()

  const [basicSlider, setBasicSlider] = useState<SliderModels.Slider>()
  const [elements, setElements] = useState<SliderModels.PPTElement[]>([])
  const [pageHeaderElements, setPageHeaderElements] = useState<SliderModels.PPTElement[]>([])
  const [pageRect, setPageRect] = useState<DOMRect>()

  const [brandIndustry, setBrandIndustry] = useState<string>(project.brandIndustry)
  const [gender, setGender] = useState<string>(project.adCustomerGender)
  const [targetMarket, setTargetMarket] = useState(project.adTargetMarket)

  const [videoCover, setVideoCover] = useState<string>('')
  const [coverSize, setCoverSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  })

  const listOne = [
    {
      label: t('dashboard.projectDetailPage.infoDrawer.projectName') + ':',
      value: project.projectName,
    },
    {
      label: t('dashboard.projectDetailPage.infoDrawer.brandName') + ':',
      value: project.brandName,
    },
    { label: t('dashboard.projectDetailPage.infoDrawer.category') + ':', value: brandIndustry },
  ]

  const listTwo = [
    { label: t('dashboard.projectDetailPage.infoDrawer.adName') + ':', value: project.adName },
    { label: t('dashboard.projectDetailPage.infoDrawer.targetMarket') + ':', value: targetMarket },
    { label: t('dashboard.projectDetailPage.infoDrawer.taGender') + ':', value: gender },
    {
      label: t('dashboard.projectDetailPage.infoDrawer.taAge') + ':',
      value: t('dashboard.projectDetailPage.infoDrawer.ageRange', {
        start: project.adCustomerAgeStart,
        end: project.adCustomerAgeEnd,
      }),
    },
  ]

  const titleRef = useRef<HTMLSpanElement>(null)
  const titleAccentRef = useRef<HTMLSpanElement>(null)
  const videoBoxRef = useRef<HTMLDivElement>(null)
  const videoCoverRef = useRef<HTMLImageElement>(null)
  const passRef = useRef<HTMLImageElement>(null)
  const listOneTitleRef = useRef<HTMLSpanElement>(null)
  const listTwoTitleRef = useRef<HTMLSpanElement>(null)
  const listOneRef = useRef<Array<HTMLSpanElement[]>>([])
  const listTwoRef = useRef<Array<HTMLSpanElement[]>>([])

  const onPageRender = (slider: SliderModels.Slider, pageRect: DOMRect) => {
    setBasicSlider(slider)
    setPageRect(pageRect)
  }

  const onPageHeaderRender = (elements: SliderModels.PPTElement[]) => {
    setPageHeaderElements(elements)
  }

  const isPortraitLayout = coverSize.height > coverSize.width

  const handleCoverLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth || img.width
    canvas.height = img.naturalHeight || img.height
    const ctx = canvas.getContext('2d')
    ctx?.drawImage(img, 0, 0, canvas.width, canvas.height)
    setCoverSize({ width: canvas.width, height: canvas.height })
    setVideoCover(canvas.toDataURL('image/png'))
  }

  useEffect(() => {
    const lng = i18n.language as keyof typeof INDUSTRY_CATEGORY_MAPS
    const industryIngMap = INDUSTRY_CATEGORY_MAPS[lng] as Record<string, string>
    setBrandIndustry(getIndustryDisplayName(project.brandIndustry, industryIngMap))
    const genderMap = GENDER_MAPS[lng] as Record<string, string>
    setGender(genderMap[project.adCustomerGender] || project.adCustomerGender)
    const targetMarketMap = TARGET_MARKTS_MAPS[lng] as Record<string, string>
    setTargetMarket(targetMarketMap[project.adTargetMarket] || project.adTargetMarket)
  }, [i18n.language])

  useEffect(() => {
    if (
      !titleRef.current ||
      !passRef.current ||
      !titleAccentRef.current ||
      !listOneTitleRef.current ||
      !listTwoTitleRef.current ||
      !listOneRef.current.length ||
      !listTwoRef.current.length ||
      !videoBoxRef.current ||
      !videoCoverRef.current ||
      !videoCover
    )
      return
    const title = titleRef.current
    const pass = passRef.current
    const listOneTitle = listOneTitleRef.current
    const listTwoTitle = listTwoTitleRef.current

    const titleRect = title.getBoundingClientRect()
    const videoBoxRect = videoBoxRef.current.getBoundingClientRect()
    const videoCoverRect = videoCoverRef.current.getBoundingClientRect()
    const passRect = pass.getBoundingClientRect()
    const listOneTitleRect = listOneTitle.getBoundingClientRect()
    const listTwoTitleRect = listTwoTitle.getBoundingClientRect()

    const SHAPE_RATIO = 13 / videoBoxRect.height
    const SHAPE_WIDTH = videoBoxRect.width
    const SHAPE_HEIGHT = videoBoxRect.height
    const SHAPE_HEIGHT_RATIO = SHAPE_HEIGHT * SHAPE_RATIO
    const SHAPE_WIDTH_DELTA = SHAPE_WIDTH - SHAPE_HEIGHT_RATIO
    const SHAPE_HEIGHT_DELTA = SHAPE_HEIGHT - SHAPE_HEIGHT_RATIO
    const buildListElements = (nodes: HTMLSpanElement[][], titleRect: DOMRect) => {
      return [
        {
          type: 'image' as 'image',
          src: commonUtils.getImagePath('PPTDownload/vertical-line.jpg'),
          x: titleRect.x - 30,
          y: titleRect.y + 65,
          width: 4,
          height: 22,
        },
        {
          type: 'text' as 'text',
          content: titleRect === listOneTitleRect ? listOneTitle.outerHTML : listTwoTitle.outerHTML,
          x: titleRect.x,
          y: titleRect.y,
          width: titleRect.width,
          height: titleRect.height,
        },
        ...nodes
          .flat()
          .map((el, index) => {
            return [
              ...(
                index % 2 === 0
                  ? [
                      {
                        type: 'shape' as 'shape',
                        x: el.getBoundingClientRect()!.x - 30,
                        y: el.getBoundingClientRect()!.y + 65,
                        width: 7,
                        height: 7,
                        path: 'M 100 0 A 50 50 0 1 1 100 200 A 50 50 0 1 1 100 0 Z',
                        viewBox: [200, 200] as [number, number],
                        fill: '#6141FA',
                        fixedRatio: false,
                        rotate: 0,
                      },
                    ]
                  : []
              ),
              {
                type: 'text' as 'text',
                content: el.outerHTML,
                x: el.getBoundingClientRect()!.x,
                y: el.getBoundingClientRect()!.y,
                width: el.getBoundingClientRect()!.width,
                height: el.getBoundingClientRect()!.height,
              },
            ]
          })
          .flat(),
      ]
    }

    const _elements: SliderModels.PPTElement[] = isPortraitLayout ? [
          {
            type: 'text',
            content: title.outerHTML,
            x: titleRect.x,
            y: titleRect.y,
            width: titleRect.width,
            height: titleRect.height,
          },
          {
            type: 'shape',
            x: videoBoxRect.x,
            y: videoBoxRect.y + 50,
            width: videoBoxRect.width,
            height: videoBoxRect.height,
            viewBox: [videoBoxRect.width, videoBoxRect.height],
            path: `M ${SHAPE_HEIGHT_RATIO} 0 L ${SHAPE_WIDTH_DELTA} 0 Q ${SHAPE_WIDTH} 0 ${SHAPE_WIDTH} ${SHAPE_HEIGHT_RATIO} L ${SHAPE_WIDTH} ${SHAPE_HEIGHT_DELTA} Q ${SHAPE_WIDTH} ${SHAPE_HEIGHT} ${SHAPE_WIDTH_DELTA} ${SHAPE_HEIGHT} L ${SHAPE_HEIGHT_RATIO} ${SHAPE_HEIGHT} Q 0 ${SHAPE_HEIGHT} 0 ${SHAPE_HEIGHT_DELTA} L 0 ${SHAPE_HEIGHT_RATIO} Q 0 0 ${SHAPE_HEIGHT_RATIO} 0 Z`,
            fill: '#2A2C3F',
            fixedRatio: false,
            rotate: 0,
            pathFormula: SliderModels.ShapePathFormulasKeys.ROUND_RECT,
            keypoints: [SHAPE_RATIO],
          },
          {
            type: 'video',
            width: videoCoverRect.width,
            height: videoCoverRect.height,
            rotate: 0,
            x: videoCoverRect.x,
            y: videoCoverRect.y + 50,
            src: project.adUrl,
            poster: videoCover,
            autoplay: false,
          },
          {
            type: 'image',
            src: pass.src,
            x: passRect.x,
            y: passRect.y + 40,
            width: passRect.width,
            height: passRect.height,
          },
          ...buildListElements(listOneRef.current, listOneTitleRect),
          ...buildListElements(listTwoRef.current, listTwoTitleRect),
        ] : [
          {
            type: 'text',
            content: title.outerHTML,
            x: titleRect.x,
            y: titleRect.y,
            width: titleRect.width,
            height: titleRect.height,
          },
          {
            type: 'shape',
            x: videoBoxRect.x,
            y: videoBoxRect.y + 50,
            width: videoBoxRect.width,
            height: videoBoxRect.height,
            viewBox: [videoBoxRect.width, videoBoxRect.height],
            path: `M ${SHAPE_HEIGHT_RATIO} 0 L ${SHAPE_WIDTH_DELTA} 0 Q ${SHAPE_WIDTH} 0 ${SHAPE_WIDTH} ${SHAPE_HEIGHT_RATIO} L ${SHAPE_WIDTH} ${SHAPE_HEIGHT_DELTA} Q ${SHAPE_WIDTH} ${SHAPE_HEIGHT} ${SHAPE_WIDTH_DELTA} ${SHAPE_HEIGHT} L ${SHAPE_HEIGHT_RATIO} ${SHAPE_HEIGHT} Q 0 ${SHAPE_HEIGHT} 0 ${SHAPE_HEIGHT_DELTA} L 0 ${SHAPE_HEIGHT_RATIO} Q 0 0 ${SHAPE_HEIGHT_RATIO} 0 Z`,
            fill: '#2a2c3f',
            fixedRatio: false,
            rotate: 0,
            pathFormula: SliderModels.ShapePathFormulasKeys.ROUND_RECT,
            keypoints: [SHAPE_RATIO],
          },
          {
            type: 'video',
            width: videoCoverRect.width,
            height: videoCoverRect.height,
            rotate: 0,
            x: videoCoverRect.x,
            y: videoCoverRect.y + 50,
            src: project.adUrl,
            poster: videoCover,
            autoplay: false,
          },
          {
            type: 'image',
            src: pass.src,
            x: passRect.x,
            y: passRect.y + 40,
            width: passRect.width,
            height: passRect.height,
          },
          ...buildListElements(listOneRef.current, listOneTitleRect),
          ...buildListElements(listTwoRef.current, listTwoTitleRect),
        ]
    const elements = _elements.map((el) => {
      return {
        ...el,
        y: el.y - 80,
      }
    })
    setElements(elements)
  }, [videoCover, isPortraitLayout, project.adUrl, brandIndustry, gender, targetMarket])

  useEffect(() => {
    if (!pageRect || !basicSlider || pageHeaderElements.length === 0 || elements.length === 0) {
      return
    }
    const _pageHeaderElements = pageHeaderElements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })
    const _elements = elements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })

    const slider: SliderModels.Slider = {
      ...basicSlider,
      elements: [..._pageHeaderElements, ...basicSlider.elements, ..._elements],
    }
    onSliderReady(slider, no)
  }, [pageRect, basicSlider, pageHeaderElements, elements])

  const sectionTitleColor = (portrait: boolean) => (portrait ? '#2B2F42' : '#6D54FA')

  const renderInfoLists = (containerClassName: string, portrait = false) => {
    return (
      <div className={containerClassName}>
        <div className={styles.list}>
          <div className={styles.title}>
            <span
              style={{
                fontSize: portrait ? '38px' : '36px',
                lineHeight: portrait ? '44px' : '42px',
                fontWeight: portrait ? 700 : 'bold',
                color: sectionTitleColor(portrait),
              }}
              ref={listOneTitleRef}
            >
              {t('dashboard.projectDetailPage.basicInformation')}
            </span>
          </div>
          {listOne.map((item, index) => {
            return (
              <div className={styles.item} key={item.label}>
                <p className={styles.label}>
                  <span
                    style={{ fontSize: 26, color: '#595959' }}
                    ref={(el: HTMLSpanElement) => {
                      if (!listOneRef.current[index]) {
                        listOneRef.current[index] = []
                      }
                      listOneRef.current[index][0] = el
                    }}
                  >
                    {item.label}
                  </span>
                </p>
                <p className={styles.value}>
                  <span
                    style={{ fontSize: 26, color: '#2a2c3f' }}
                    ref={(el: HTMLSpanElement) => {
                      if (!listOneRef.current[index]) {
                        listOneRef.current[index] = []
                      }
                      listOneRef.current[index][1] = el
                    }}
                  >
                    {item.value}
                  </span>
                </p>
              </div>
            )
          })}
        </div>
        <div className={styles.list} style={{ marginTop: portrait ? '64px' : '86px' }}>
          <div className={styles.title}>
            <span
              style={{
                fontSize: portrait ? '38px' : '36px',
                lineHeight: portrait ? '44px' : '42px',
                fontWeight: portrait ? 700 : 'bold',
                color: sectionTitleColor(portrait),
              }}
              ref={listTwoTitleRef}
            >
              {t('dashboard.projectDetailPage.infoDrawer.advertisingInformation')}
            </span>
          </div>
          {listTwo.map((item, index) => {
            return (
              <div className={styles.item} key={item.label}>
                <p className={styles.label}>
                  <span
                    style={{ fontSize: 26, color: '#595959' }}
                    ref={(el: HTMLSpanElement) => {
                      if (!listTwoRef.current[index]) {
                        listTwoRef.current[index] = []
                      }
                      listTwoRef.current[index][0] = el
                    }}
                  >
                    {item.label}
                  </span>
                </p>
                <p className={styles.value}>
                  <span
                    style={{ fontSize: 26, color: '#2a2c3f' }}
                    ref={(el: HTMLSpanElement) => {
                      if (!listTwoRef.current[index]) {
                        listTwoRef.current[index] = []
                      }
                      listTwoRef.current[index][1] = el
                    }}
                  >
                    {item.value}
                  </span>
                </p>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <PptPage
      no={no}
      bgSrc={commonUtils.getImagePath('PPTDownload/bg-4.jpg')}
      onPageRender={onPageRender}
    >
      <div className={styles.pptPage4}>
        <PageHeader
          project={project}
          text={'01.' + t('dashboard.projectDetailPage.basicInformation')}
          onRender={onPageHeaderRender}
          titleOffsetX={32}
        />
        <div className={cls(styles.content, { [styles.portraitContent]: isPortraitLayout })}>
          {isPortraitLayout ? (
            <>
              <div className={styles.portraitLeft}>
                <div className={styles.portraitTitle}>
                  <span className={styles.portraitTitleAccent} ref={titleAccentRef}></span>
                  <span
                    className={cls(styles.portraitTitleText, styles.portraitTitleTextWide)}
                    style={{ fontSize: '64px', lineHeight: '90px', fontWeight: 600 }}
                    ref={titleRef}
                  >
                    {project.adName}
                  </span>
                </div>
                {renderInfoLists(styles.portraitInfo, true)}
              </div>
              <div className={styles.portraitRight}>
                <div className={styles.portraitVideoWrap}>
                  <div className={styles.portraitVideoCard} ref={videoBoxRef}>
                    <div className={styles.portraitVideoInner}>
                      <img
                        className={styles.cover}
                        ref={videoCoverRef}
                        src={project.coverPath}
                        crossOrigin='anonymous'
                        onLoad={handleCoverLoad}
                      />
                    </div>
                  </div>
                  <img
                    className={styles.portraitPass}
                    ref={passRef}
                    style={{
                      width: 198,
                      height: 198,
                    }}
                    src={
                      project.globalMetrics.overall.score >= project.globalMetrics.overall.meanScore
                        ? commonUtils.getImagePath('PDFDownload/pdf-page-4/pass.png')
                        : commonUtils.getImagePath('PDFDownload/pdf-page-4/not-pass.png')
                    }
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles.left}>
                <p className={styles.title}>
                  <span
                    ref={titleAccentRef}
                    style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }}
                  ></span>
                  <span
                    style={{ fontSize: '64px', lineHeight: '90px', fontWeight: 'bold' }}
                    ref={titleRef}
                  >
                    {project.adName}
                  </span>
                </p>
                <div className={styles.info} ref={videoBoxRef}>
                  <img
                    className={styles.pass}
                    ref={passRef}
                    style={{
                      width: 198,
                      height: 198,
                    }}
                    src={
                      project.globalMetrics.overall.score >= project.globalMetrics.overall.meanScore
                        ? commonUtils.getImagePath('PDFDownload/pdf-page-4/pass.png')
                        : commonUtils.getImagePath('PDFDownload/pdf-page-4/not-pass.png')
                    }
                  />
                  <div className={styles.coverWrap}>
                    <img
                      className={styles.cover}
                      style={{
                        width: 782,
                        height: 440,
                      }}
                      ref={videoCoverRef}
                      src={project.coverPath}
                      crossOrigin='anonymous'
                      onLoad={handleCoverLoad}
                    />
                  </div>
                </div>
              </div>
              {renderInfoLists(styles.right)}
            </>
          )}
        </div>
      </div>
    </PptPage>
  )
}

export default PptPage4
